import {
  KnownImpressionTypes,
  KNOWN_IMPRESSION_TYPES,
} from '../AITools/ImpressionEditor/ImpressionItem';

export const AI_GENERATED_SYSTEM =
  'https://lifeomic.com/fhir/action-plan-ai-generated';

export const AI_GENERATED_SYSTEM_V2 =
  'https://lifeomic.com/fhir/fountain-life-ai-generated';

export const isAiGenerated = (
  item: fhir.CarePlanActivity['detail'] | fhir.ClinicalImpression,
): boolean => {
  return (
    item?.extension?.some(
      ext =>
        (ext?.url === AI_GENERATED_SYSTEM ||
          ext?.url === AI_GENERATED_SYSTEM_V2) &&
        ext?.valueBoolean === true,
    ) ?? false
  );
};

/**
 * Best-effort timestamp for comparing annual report ({@link fhir.CarePlan}) versions.
 * Prefer server `meta.lastUpdated`, then period end, then period start.
 */
export const getCarePlanRecencyTime = (carePlan: fhir.CarePlan): number => {
  const lastUpdated = carePlan.meta?.lastUpdated;
  if (lastUpdated) {
    const t = new Date(lastUpdated).getTime();
    if (!Number.isNaN(t)) {
      return t;
    }
  }
  const end = carePlan.period?.end;
  if (end) {
    const t = new Date(end).getTime();
    if (!Number.isNaN(t)) {
      return t;
    }
  }
  const start = carePlan.period?.start;
  if (start) {
    const t = new Date(start).getTime();
    if (!Number.isNaN(t)) {
      return t;
    }
  }
  return 0;
};

const TERMINAL_ANNUAL_REPORT_STATUSES = new Set<fhir.CarePlan['status']>([
  'completed',
  'entered-in-error',
]);

/**
 * When viewing a terminal (completed / entered-in-error) annual report, returns the
 * newest active CarePlan that is newer than `currentCarePlan`.
 * Care plans without `supportingInfo` are ignored (same filter as executive summary loading).
 */
export const findNewerActiveAnnualReport = ({
  currentCarePlan,
  carePlans,
}: {
  currentCarePlan: fhir.CarePlan;
  carePlans: fhir.CarePlan[];
}): fhir.CarePlan | undefined => {
  const status = currentCarePlan.status;
  if (
    !status ||
    !TERMINAL_ANNUAL_REPORT_STATUSES.has(status) ||
    !currentCarePlan.id
  ) {
    return undefined;
  }

  const currentRecency = getCarePlanRecencyTime(currentCarePlan);
  const terminalEnd = currentCarePlan.period?.end;

  const isNewerActive = (candidate: fhir.CarePlan): boolean => {
    if (
      candidate.status !== 'active' ||
      candidate.id === currentCarePlan.id ||
      !candidate.supportingInfo?.length
    ) {
      return false;
    }
    const candidateRecency = getCarePlanRecencyTime(candidate);
    if (candidateRecency > currentRecency) {
      return true;
    }
    const activeStart = candidate.period?.start;
    if (activeStart && terminalEnd) {
      return new Date(activeStart).getTime() >= new Date(terminalEnd).getTime();
    }
    return false;
  };

  const newerActives = carePlans.filter(isNewerActive);
  if (!newerActives.length) {
    return undefined;
  }

  return newerActives.reduce((best, cp) =>
    getCarePlanRecencyTime(cp) > getCarePlanRecencyTime(best) ? cp : best,
  );
};

export const orderImpressionTypes = (
  impressionTypes: KnownImpressionTypes[],
) => {
  // we cast because we want to preserve any that are not allowed (backward compatibility)
  return impressionTypes.sort((impressionTypeA, impressionTypeB) => {
    const order =
      KNOWN_IMPRESSION_TYPES.indexOf(impressionTypeA) -
      KNOWN_IMPRESSION_TYPES.indexOf(impressionTypeB);
    return order;
  });
};

/**
 * Matches a Markdown footnote reference. This will also match the first part of a
 * footnote definition.
 *
 * The citation payload (after the second colon) may be any non-`]` string — including
 * knowledge-base `s3://…/.files-kb-…` URIs — aligned with Lexical attachment import syntax.
 */
export const footnoteRegexp =
  /(\s*\[footnote_ref:\d+:[^\]]*\](?!:))|(\s*\[\^[A-Za-z0-9_]+\](?!:))/g;

/**
 * Matches a markdown footnote definition.
 */
export const footnoteDefinitionRegexp =
  /^(?:\[footnote_ref:\d+:[^\]]*\]|\[\^[A-Za-z0-9_]+\]):.*$/gm;
